/* eslint max-lines: 0 */
import { ComponentStyleConfig } from '@chakra-ui/react';
import { transparentize } from '@chakra-ui/theme-tools';

const PREFIX = 'TableList';

export const classes = {
  accessoryCell: `${PREFIX}-accessory-cell`,
  headerCell: `${PREFIX}-header-cell`,
  centeredCell: `${PREFIX}-centered-cell`,
  cell: `${PREFIX}-cell`,
  rightAlignedCell: `${PREFIX}-right-alignedCell`,
  rightAlignEllipsis: `${PREFIX}-rightAlignEllipsis`,
  row: `${PREFIX}-row`,
  rowCollapsed: `${PREFIX}-row-collapsed`,
  rowClickable: `${PREFIX}-row-clickable`,
  rowSelected: `${PREFIX}-row-selected`,
  subHeader: `${PREFIX}-subheader`,
  subHeaderLoader: `${PREFIX}-subheader-loader`,
  subHeaderContentRow: `${PREFIX}-subheader-content-row`,
  rowDisabled: `${PREFIX}-row-disabled`,
  hideBorder: `${PREFIX}-hide-border`,
  customContentRow: `${PREFIX}-customContentRow`,
  scrollable: `${PREFIX}-scrollable`,
  rowDraggable: `${PREFIX}-rowDraggable`,
  rowDragging: `${PREFIX}-rowDragging`,
  rowOver: `${PREFIX}-rowOver`,
  rowOverInset: `${PREFIX}-rowOverInset`,
  rowHover: `${PREFIX}-rowHover`,
  noHeader: `${PREFIX}-noHeader`,
};

export const columnMargin = 24;

export const TableList: ComponentStyleConfig = {
  baseStyle: {
    '.rdg': {
      height: '100%',
      position: 'relative',
      background: 'transparent',
      border: 'none',
      overflowY: 'hidden',
      overflowX: 'hidden',
      '.rdg-header-row': {
        backgroundColor: 'transparent',
      },
      '.rdg-row': {
        backgroundColor: 'transparent',
      },
      '.rdg-header-row .rdg-cell': {},
      '.rdg-cell': {
        border: 'none',
        boxShadow: 'none',
        '&:first-of-type': {
          paddingLeft: 1,
        },
      },
      '.rdg-summary-row': {
        backgroundColor: 'M.30',
        border: '1px solid',
        borderColor: 'borderSubdued',
      },
    },

    [`.${classes.scrollable} .rdg`]: {
      overflowY: 'auto',
      '.rdg-header-row': {
        bg: 'surface2',
      },
    },

    [`.${classes.centeredCell}`]: {
      textAlign: 'center',
    },

    [`.${classes.accessoryCell}`]: {
      textOverflow: 'unset',
      '& + .rdg-cell': {
        paddingLeft: 0,
      },
    },

    [`.${classes.headerCell}`]: {
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 12,
      letterSpacing: '0.8px',
      backgroundColor: 'transparent',
      color: 'textSubdued',
      textOverflow: 'hidden',
    },

    [`.${classes.noHeader}`]: {
      opacity: 0,
    },

    [`.${classes.rightAlignedCell}`]: {
      display: 'flex',
      justifyContent: 'flex-end',

      [`&.${classes.cell}`]: {
        paddingRight: 0,
      },
    },

    [`.${classes.rightAlignEllipsis}`]: {
      width: '100%',
      textAlign: 'left',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      justifyContent: 'flex-end',
    },

    [`.${classes.cell}`]: {
      paddingLeft: `${columnMargin / 2}px`,
      paddingRight: `${columnMargin / 2}px`,
    },
    [`.${classes.row}`]: {
      bg: 'surface2',
      _odd: {
        bg: transparentize('surface2', 0.36),
      },
      ' .rdg-cell': {
        color: 'textBase',
      },
      '&:hover': {
        bg: transparentize('surface2', 0.72),
        ' .options': {
          path: {
            fill: 'iconHighlighted',
          },
        },
      },
      [`&.${classes.subHeader}`]: {
        bg: 'surface1',
        _hover: {
          backgroundColor: 'transparent',
        },
        '.rdg-cell': {
          paddingRight: 0,
          border: 'none',
        },
      },
      [`&.${classes.rowDisabled}`]: {
        opacity: 0.36,
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
      [`&.${classes.rowDraggable}`]: {
        _active: {
          cursor: 'grabbing',
          userSelect: 'none',
        },
      },
      [`&.${classes.rowDraggable}:not(.${classes.rowClickable})`]: {
        _hover: {
          cursor: 'grab',
        },
      },
      [`&.${classes.rowDragging}`]: {
        backgroundColor: transparentize('M.10', 0.36),
      },
      [`&.${classes.rowOver}`]: {
        boxShadow: `0px 1px 0px 0px var(--chakra-colors-B-80)`,
      },
      [`&.${classes.rowOverInset}`]: {
        boxShadow: `inset 0px 1px 0px 0px var(--chakra-colors-B-80)`,
      },
      [`&.${classes.rowHover}:hover, &.${classes.rowHover}`]: {
        backgroundColor: 'transparent !important',
        cursor: 'grabbing !important',
      },
      [`&.${classes.rowSelected}`]: {
        backgroundColor: transparentize('M.30', 0.36),
      },
    },

    [`.${classes.hideBorder} .rdg-cell`]: {
      border: 'none',
    },

    [`.${classes.rowClickable}`]: {
      cursor: 'pointer',
    },

    [`.${classes.subHeaderContentRow}`]: {
      '> .rdg-cell': {
        padding: 0,
        borderBottom: 'none',
      },
      '&:hover': {
        background: 'transparent',
      },
    },
    [`.${classes.subHeaderLoader} .rdg-cell`]: {
      paddingRight: 0,
      border: 'none',
    },

    [`.${classes.customContentRow}:hover`]: {
      background: 'transparent',
    },
  },
  variants: {
    loading: {
      [`.${classes.row}`]: {
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
      [`.${classes.rowClickable}:hover`]: {
        cursor: undefined,
      },
    },
    scrollable: {
      '.rdg': {
        overflowY: 'auto',
        '.rdg-header-row': {
          backgroundColor: 'surface1',
        },
      },
    },
    playerPlaylist: {
      [`.${classes.row}`]: {
        ' .rdg-cell': {
          color: 'textBase',
        },
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
      '.rdg .rdg-header-row .rdg-cell': {},
    },
    default: {},
  },
  sizes: {},
  defaultProps: {},
};
